/**
 * Unified auth hook - provides abstraction layer for authentication.
 * Switches between Auth0 and self-managed auth based on auth provider configuration.
 * Supports runtime switching via URL parameter (?auth=self-managed) or localStorage.
 */
import { AuthProvider } from '@40seas/entities/auth';
import { useSafeAuth0 } from '../providers/SafeAuth0Provider';
import { useAuth as useAuthV2, getAuthProvider, isAuthTransitionExpired, resetCachedProvider } from '@40seas/auth-client';

export interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    user:
        | {
              email?: string;
              name?: string;
              picture?: string;
              sub?: string;
              [key: string]: unknown;
          }
        | undefined;
    loginWithRedirect: (options?: { appState?: { returnTo?: string } }) => Promise<void>;
    logout: (options?: { returnTo?: string }) => void;
    getAccessTokenSilently: (options?: { ignoreCache?: boolean }) => Promise<string>;
}

/**
 * Unified auth hook that abstracts the underlying auth provider.
 * Both hooks are called unconditionally to respect React's Rules of Hooks.
 * useSafeAuth0 and useAuthV2 both provide safe defaults when used
 * outside their respective providers, allowing this pattern to work.
 */
export const useAuth = (): AuthState => {
    // Always call both hooks (React Rules of Hooks require consistent hook calls)
    // Both hooks provide safe defaults when used outside their providers
    const auth0 = useSafeAuth0();
    const authV2 = useAuthV2();

    // Get auth provider at render time to support runtime switching
    const authProvider = getAuthProvider();

    if (authProvider === AuthProvider.SelfManaged) {
        return {
            isAuthenticated: authV2.isAuthenticated,
            isLoading: authV2.isLoading,
            user: authV2.user
                ? {
                      email: authV2.user.email,
                      name: authV2.user.name ?? undefined,
                      picture: authV2.user.picture ?? undefined,
                      sub: authV2.user.id,
                  }
                : undefined,
            loginWithRedirect: authV2.loginWithRedirect,
            logout: authV2.logout,
            getAccessTokenSilently: authV2.getAccessTokenSilently,
        };
    }

    // Default: Auth0
    return {
        isAuthenticated: auth0.isAuthenticated,
        isLoading: auth0.isLoading,
        user: auth0.user,
        loginWithRedirect: auth0.loginWithRedirect,
        logout: (options) => auth0.logout({ returnTo: options?.returnTo }),
        getAccessTokenSilently: async (options) => {
            // If we're in a provider transition and the deadline has passed,
            // block the token refresh so the user is forced to re-login
            // with the new auth provider.
            if (isAuthTransitionExpired()) {
                // Don't clear the transition key here — it's needed on the login
                // page to trigger the "forgot password" flow for migrated users.
                // It gets cleared after successful self-managed login (loginProvider.ts).
                resetCachedProvider();
                // Redirect to /login so getLoginHint() returns 'login'
                // instead of the default 'signup'
                window.location.pathname = '/login';
                throw new Error('Auth session expired — redirecting to login');
            }
            return auth0.getAccessTokenSilently(options);
        },
    };
};
